import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import * as changeCase from "change-case";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { EqualColumnTable } from "../../components/equal_column_table/EqualColumnTable";
import { ResultCard } from "../../components/result_card/ResultCard";
import {
  TemperatureUnitType,
  calculateTapped,
  inputTypeOnChanged,
  inputValueOnChanged,
  outputTypeOnChanged,
  resultCopyTapped,
  selectState,
} from "./TemperatureConverterSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2";
import { HomeCard } from "../../components/home_card/HomeCard";
import DistanceUnitConverterImage from "../../assets/DistanceUnitConverter.webp";
import WeightUnitConverterImage from "../../assets/WeightUnitConverter.webp";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function TemperatureConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("temperatureConverter.title")}
      pageSeoTitle={t("temperatureConverter.seoTitle")}
      pageDescription={t("temperatureConverter.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("temperatureConverter.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("temperatureConverter.fromLabel")}
            value={state.inputUnit}
            onChange={(e) =>
              dispatch(
                inputTypeOnChanged(
                  Number(e.target.value) as TemperatureUnitType
                )
              )
            }
          >
            <MenuItem value={TemperatureUnitType.celcius}>{t("temperatureConverter.options.celsius")}</MenuItem>
            <MenuItem value={TemperatureUnitType.fahrenheit}>{t("temperatureConverter.options.fahrenheit")}</MenuItem>
            <MenuItem value={TemperatureUnitType.kelvin}>{t("temperatureConverter.options.kelvin")}</MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("temperatureConverter.toLabel")}
            value={state.outputUnit}
            onChange={(e) =>
              dispatch(
                outputTypeOnChanged(
                  Number(e.target.value) as TemperatureUnitType
                )
              )
            }
          >
            <MenuItem value={TemperatureUnitType.celcius}>{t("temperatureConverter.options.celsius")}</MenuItem>
            <MenuItem value={TemperatureUnitType.fahrenheit}>{t("temperatureConverter.options.fahrenheit")}</MenuItem>
            <MenuItem value={TemperatureUnitType.kelvin}>{t("temperatureConverter.options.kelvin")}</MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("temperatureConverter.valueLabel")}
            variant="outlined"
            color="info"
            value={state.inputValue || ""}
            onChange={(e) =>
              dispatch(inputValueOnChanged(Number(e.target.value)))
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>{state.inputSymbol}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 1, mb: 3 }}>
          <CustomButton
            label={t("temperatureConverter.convertButton")}
            onClick={() => {
              dispatch(calculateTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.outputValue}`}
            symbol={`${state.resultSymbol}`}
            description={changeCase.pascalCase(
              TemperatureUnitType[state.resultUnit]
            )}
            onCopy={() => dispatch(resultCopyTapped())}
            resultCopied={state.resultCopied}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>
        <Grid2 xs={12}>
          <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("temperatureConverter.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("temperatureConverter.content.freeDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("temperatureConverter.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("temperatureConverter.content.howToDescription")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("temperatureConverter.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="distanceConverter"
            link="/DistanceUnitConverter"
            smallCard={true}
            image={DistanceUnitConverterImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="weightConverter"
            link="/WeightUnitConverter"
            smallCard={true}
            image={WeightUnitConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
